import React, {createContext, useContext, useCallback} from "react"
import {useQuery} from "react-query";
import {apiV2} from "../../api/axiosConfig";
import {useAuth} from "@/context/user";

type Module = "siren_config" | "siren_resources" | "siren_alert" | "contacts"
type ModulePermissions = Partial<Record<Module, string[]>> // e.g. { siren: ["read"], contacts: ["full_access"] }

type PermissionsContextType = {
    loading: boolean
    permissions: ModulePermissions
    reload: () => void
    hasPermission: (module: Module, permission: string) => boolean
    hasFullAccess: (module: Module) => boolean
}

const PermissionsContext = createContext<PermissionsContextType | null>(null)

export function PermissionsProvider({children}: { children: React.ReactNode }) {

    const [initialLoaded, setInitialLoaded] = React.useState(false)

    const {isAuthenticated} = useAuth()

    const parsePermissions = (arr: string[]): ModulePermissions => {
        const map: ModulePermissions = {}

        arr.forEach(item => {
            const [module, perm] = item.split(":")
            if (!module || !perm) return

            const perms = map[module as Module] ?? []
            perms.push(perm)
            map[module as Module] = perms
        })

        return map
    }

    const {
        data: permissions = {},
        refetch: reload,
        isFetching,
    } = useQuery<ModulePermissions>(
        ["module-permissions"],
        async () => {
            if (!isAuthenticated) return parsePermissions([])

            const {data} = await apiV2.get<string[]>("session/module-permissions")
            return parsePermissions(data || [])

        },
        {
            refetchInterval: 60_000,
            refetchOnWindowFocus: true,
            staleTime: 59_000,
            onSuccess: () => setInitialLoaded(true),
            onError: () => setInitialLoaded(true),
        }
    )

    const hasFullAccess = useCallback(
        (module: Module) => permissions[module]?.includes("full_access") ?? false,
        [permissions]
    )

    const hasPermission = useCallback(
        (module: Module, permission: string) =>
            !!(hasFullAccess(module) || permissions[module]?.includes(permission)),
        [permissions, hasFullAccess]
    )

    if (!initialLoaded) return null

    return (
        <PermissionsContext.Provider
            value={{
                loading: isFetching,
                permissions,
                reload,
                hasPermission,
                hasFullAccess,
            }}
        >
            {children}
        </PermissionsContext.Provider>
    )
}

export function usePermissions() {
    const ctx = useContext(PermissionsContext)
    if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider")
    return ctx
}
